import { Component, HostListener, inject, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule, Location } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { WaiterService } from '../../_service/waiter.service';
import { CartComponent } from '../../common/cart/cart.component';
import { FormsModule } from '@angular/forms';
import { trigger, transition, style, animate, stagger, query } from '@angular/animations';

@Component({
  selector: 'app-inventory',
  standalone: true,
  imports: [RouterModule, CommonModule, CartComponent, FormsModule],
  templateUrl: './inventory.component.html',
  styleUrl: './inventory.component.scss',
  animations: [
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(30px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('staggerAnimation', [
      transition('* <=> *', [
        query(':enter', [
          style({ opacity: 0, transform: 'translateY(50px)' }),
          stagger('100ms', [
            animate('400ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ])
    ]),
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class InventoryComponent implements OnInit {
  showScroll: boolean = false;
  private modalService = inject(NgbModal);
  orderId!: string;  // To store the 'id' parameter
  categoryId!: string;  // To store the 'catid' parameter
  total = 0;
  items: any[] = []; // Store fetched items
  page = 1; // Start from page 1
  loading = false; // Flag to handle loading state
  isInventoryLoading = false; // Loading state for inventory items
  isSearchLoading = false; // Loading state for search
  isCategoryLoading = false; // Loading state for category details
  isOrderDetailsLoading = false; // Loading state for order details
  totalPages: number = 0; // Will store total pages from API response
  id: any = '';
  category: any;
  cartItems: any[] = [];
  showPopup: boolean = false;
  noTable: boolean = false;
  searchTerm: string = '';
  filteredItems: any[] = [];
  tempItem: any;
  orderDetails: any;

  // Skeleton loader arrays
  skeletonItems = Array(8).fill(0);

  constructor(
    config: NgbModalConfig,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private app: WaiterService
  ) {
    this.route.params.subscribe(params => {
      this.orderId = params['id'];
      this.categoryId = params['catid'];
    });

    config.centered = true
  }

  // Track by function for better performance
  trackByItem(index: number, item: any): any {
    return item.id || index;
  }

  // Image error handler
  onImageError(event: any): void {
    event.target.src = 'public/cook.png'; // Fallback image
  }
  ngOnInit(): void {
    this.isOrderDetailsLoading = true;
    this.isCategoryLoading = true;
    this.getOrderDetails();
    this.fetchCategoryDetails();
  }

  onSearch(event: any): void {
    const term = event.target.value.toLowerCase();
    this.searchTerm = term;
    this.isSearchLoading = true;

    // Simulate search delay for better UX
    setTimeout(() => {
      this.filteredItems = this.filterItems(term);
      this.isSearchLoading = false;
    }, 300);
  }

  private filterItems(term: string): any[] {
    if (!term) return this.items;

    return this.items.filter(item =>
      item.name.toLowerCase().includes(term.toLowerCase()) ||
      (item.category && item.category.name.toLowerCase().includes(term.toLowerCase()))
    );
  }
  getOrderDetails() {
    this.isOrderDetailsLoading = true;
    this.app.viewOrder(this.orderId).subscribe({
      next: (res) => {
        this.orderDetails = res;
        this.fetchAllCategoryItems(this.orderDetails.barcode_details.uid);
      },
      error: (err) => {
        console.error(err);
        this.isOrderDetailsLoading = false;
      },
      complete: () => {
        this.isOrderDetailsLoading = false;
      }
    })
  }
  loadCartItems() {
    const cart = localStorage.getItem('cart');
    if (cart) {
      this.cartItems = JSON.parse(cart);
    } else {
      this.cartItems = [];
    }
  }
  addToCartModal(item: any, content: any) {
    this.tempItem = item;
    this.modalService.open(content, { centered: true, size: 'md' })
  }
  // Increment quantity for a specific cart item
  incrementCart(item: any) {
    item.quantity += 1;
    this.updateCart();
  }

  // Decrement quantity for a specific cart item
  decrementCart(item: any) {
    if (item.quantity > 1) {
      item.quantity -= 1;
      this.updateCart();
    }
  }
  updateCart() {
    localStorage.setItem('cart', JSON.stringify(this.cartItems));
  }
  // Remove an item from the cart
  removeItem(item: any) {
    this.cartItems = this.cartItems.filter(cartItem => cartItem.id !== item.id);
    this.updateCart();
    this.showPopup = false;
  }
  // Calculate the total amount for the cart
  getTotalAmount(): number {
    return this.cartItems.reduce((total, item) => total + (item.selling_price * item.quantity), 0);
  }

  cart(content: any) {
    // this.loadCartItems()
    this.modalService.open(content, { centered: true }).result
  }

  fetchAllCategoryItems(tableId: any) {
    if (this.loading) return; // Prevent multiple calls
    this.loading = true;
    this.isInventoryLoading = true;

    // Fetch existing cart from localStorage
    let cart = JSON.parse(localStorage.getItem('cart') || '[]');

    this.app.fetchInventories(this.page, this.categoryId, tableId).subscribe({
      next: (res: any) => {
        // Add a quantity property and inCart flag for each item
        const newItems = res.data.map((item: any) => {
          const cartItem = cart.find((cartItem: any) => cartItem.id === item.id); // Check if item is in cart
          const inCart = !!cartItem; // Set inCart flag
          const userQuantity = cartItem ? cartItem.quantity : 0; // Set userQuantity based on cart
          return { ...item, userQuantity, inCart }; // Add userQuantity and inCart fields
        });
        this.items = [...this.items, ...newItems];
        this.filteredItems = this.filterItems(this.searchTerm);
        this.totalPages = res.meta.last_page;
      },
      error: (err: any) => {
        console.error(err);
      },
      complete: () => {
        this.loading = false;
        this.isInventoryLoading = false;
      }
    });
  }

  fetchCategoryDetails() {
    this.isCategoryLoading = true;
    this.app.fetchCategoryDetails(this.categoryId).subscribe({
      next: (res: any) => {
        this.category = res;
      },
      error: (err: any) => {
        console.error(err);
      },
      complete: () => {
        this.loading = false;
        this.isCategoryLoading = false;
      }
    });
  }
  capitalizeFirstLetter(word: string) {
    if (!word) return '';  // Return an empty string if the input is not valid
    return word.charAt(0).toUpperCase() + word.slice(1);
  }
  // Increment quantity for a specific item
  // Increment quantity for a specific item
  increment(item: any) {
    if (item.userQuantity == 0) {
      item.userQuantity = 1;
      this.addToCart(item);
      return;
    }
    // Increase quantity in UI
    item.userQuantity += 1;

    // Retrieve the cart from localStorage
    let cart = JSON.parse(localStorage.getItem('cart') || '[]');

    // Find the item in the cart
    let cartItem = cart.find((cartItem: any) => cartItem.id === item.id);

    // Update the quantity in the cart
    if (cartItem) {
      cartItem.quantity = item.userQuantity;
    }

    // Save the updated cart back to localStorage
    localStorage.setItem('cart', JSON.stringify(cart));

    this.toastr.success(`${item.name} quantity has been increased`, 'Quantity Updated', {
      timeOut: 2000
    });
  }

  // Decrement quantity for a specific item
  decrement(item: any) {
    if (item.userQuantity > 1) {
      // Decrease quantity in UI
      item.userQuantity -= 1;

      // Retrieve the cart from localStorage
      let cart = JSON.parse(localStorage.getItem('cart') || '[]');

      // Find the item in the cart
      let cartItem = cart.find((cartItem: any) => cartItem.id === item.id);

      // Update the quantity in the cart
      if (cartItem) {
        cartItem.quantity = item.userQuantity;
      }

      // Save the updated cart back to localStorage
      localStorage.setItem('cart', JSON.stringify(cart));

      this.toastr.success(`${item.name} quantity has been decreased`, 'Quantity Updated', {
        timeOut: 2000
      });

    } else {
      // If userQuantity reaches 0, remove the item from the cart
      item.userQuantity = 0;
      item.inCart = false; // Mark as not in cart

      // Retrieve the cart from localStorage
      let cart = JSON.parse(localStorage.getItem('cart') || '[]');

      // Filter out the item from the cart
      cart = cart.filter((cartItem: any) => cartItem.id !== item.id);

      // Save the updated cart back to localStorage
      localStorage.setItem('cart', JSON.stringify(cart));

      this.toastr.info(`${item.name} has been removed from your cart`, 'Item Removed', {
        timeOut: 2000
      });
    }
  }



  addToCart(item: any) {
    if (item.quantity === 0) {
      this.toastr.warning(`${item.name} is out of stock`, 'Out of Stock', {
        timeOut: 2000
      });
      return; // Stop if item is out of stock
    }

    if (item.inCart) {
      return; // Do nothing if item is already in cart
    }

    // Retrieve the existing cart from localStorage (or initialize as an empty array)
    let cart = JSON.parse(localStorage.getItem('cart') || '[]');

    // Add the new item to the cart
    cart.push({
      id: item.id,
      name: item.name,
      quantity: item.userQuantity,
      selling_price: item.selling_price,
      main_image: item.main_image,
    });

    // Save the updated cart back to localStorage
    localStorage.setItem('cart', JSON.stringify(cart));

    // Mark the item as added to the cart
    item.inCart = true;

    this.toastr.success(`${item.name} has been added to your cart`, 'Item Added', {
      timeOut: 2000
    });
  }


  // Infinite scroll detection
  @HostListener('window:scroll', ['$event'])
  onScroll() {
    if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.loading) {
      if (this.page < this.totalPages) {
        this.page++; // Increment page
        this.fetchAllCategoryItems(this.orderDetails.barcode_details.uid); // Fetch next page
      }
    }
  }

  AddToOrder() {
    const data = {
      order_items: this.cartItems
    }

  }

}
